// Los pasajeros del widget: habitaciones, adultos, niños, bebés y sus edades.
//
// Es la pieza con más reglas propias, y todas salen del JS de la home
// (inputsPasajerosSumar / inputsPasajerosRestar / edadesNinos* / showRooms):
//
//   - cada habitación admite como mucho 9 personas entre adultos, niños y bebés;
//   - los topes de cada contador salen del atributo `max` del markup (adultos 1-4,
//     niños 0-4, bebés 0-2), no de un número escrito aquí;
//   - los BEBÉS no pueden pasar del número de adultos: un bebé va en brazos de alguien, y al
//     bajar adultos hay que bajar bebés con ellos;
//   - cada niño y cada bebé pide su edad, y mientras falte una edad el botón "Listo" no se
//     puede pulsar (en la home se OCULTA; aquí se deshabilita, que se entiende mejor y no
//     deja al cliente buscando un botón que ha desaparecido);
//   - el orden de las edades importa y no es el visual: primero las de los bebés (tren) y
//     después las de los niños (hotel). Así sale en la URL de compra, y así se replica.
//
// El resumen ("2 habitaciones, 4 adultos, 1 niño") se arma con los plurales que la home
// esconde en <span id="texto_*"> y que aquí son datos.
import { modalConPlantilla } from './modales.js'

const TOPE_POR_HABITACION = 9
const EDAD_MAX_NINO = 13     // el niño de hotel
const EDAD_MAX_BEBE = 3      // el "niño sin plaza" del tren

/**
 * @param {import('./buscador.js').Buscador} buscador
 * @param {string} bloque
 */
export function crearPasajeros (buscador, bloque) {
  // El botón sólo existe donde los pasajeros son un desplegable. En "Tren + Hotel / Ocio" no
  // hay botón porque los pasajeros se piden dentro del flujo de búsqueda, así que aquí no se
  // puede devolver null: quien los abre es busqueda.js llamando a abrir().
  const boton = buscador.nodo(bloque, '.rvb-btn-pasajeros')
  const estado = buscador.estadoDe(bloque)
  // Hay DOS formas de pedir los pasajeros, y el markup dice cuál toca:
  //
  //   - "Tren + Hotel + Ocio" y "Hotel" traen su propio .pasajeros-desplegable DENTRO del
  //     bloque: ahí no hay modal, se abre y se cierra en la misma barra;
  //   - "Tren + Hotel / Ocio" no tiene ninguno (su botón es .btn-abrir-swal): los pasajeros
  //     se preguntan DESPUÉS de pulsar Buscar, en un modal, y sólo si el cliente dice que sí
  //     al hotel. Eso es parte de la secuencia de salida y lo monta F3 llamando a abrir().
  //
  // Que cada uno arranque con distinto número de adultos (2 el de hotel, 1 el de ocio) no es
  // cosmético: cambia el numTicketsAdults de la URL según por dónde entre el cliente.
  // Cuál de los dos modales se abre. El flujo de búsqueda lo cambia sobre la marcha: el que
  // dice que sí al hotel va al de tren+hotel (2 adultos de serie) y el que dice que no, al de
  // tren+ocio (1 adulto). De ahí sale el numTicketsAdults de la URL.
  let plantillaActual = bloque === 'bloqueActividades' ? 'menuPasajerosTrenOcio' : 'menuPasajerosTrenHotel'
  let alTerminar = null
  const enLinea = buscador.nodo(bloque, '.rvb-pasajeros-desplegable')
  let modal = null
  // El sitio donde vive el contenido de pasajeros ahora mismo: el desplegable del bloque o el
  // del modal. Todo lo demás trabaja contra esto y le da igual cuál sea.
  let panel = enLinea || null

  const leer = (el, selector) => el.querySelector(selector)
  const num = (input) => Number(input && input.value ? input.value : 0)

  /** Las tres cuentas de una habitación. */
  function contadores (habitacion) {
    return {
      adultos: leer(habitacion, '.rvb-adultsAmount'),
      ninos: leer(habitacion, '.rvb-childrenAmount'),
      bebes: leer(habitacion, '.rvb-babiesAmount'),
    }
  }

  function totalDe (habitacion) {
    const c = contadores(habitacion)
    return num(c.adultos) + num(c.ninos) + num(c.bebes)
  }

  /** Sincroniza el número que se ve con el valor del input oculto, que es el que manda. */
  function pintarHabitacion (habitacion) {
    const c = contadores(habitacion)
    const pares = [
      [c.adultos, '.rvb-num-adults'],
      [c.ninos, '.rvb-num-children'],
      [c.bebes, '.rvb-num-babies'],
    ]
    for (const [input, selector] of pares) {
      const visible = leer(habitacion, selector)
      if (input && visible) visible.textContent = String(num(input))
    }

    // Un bebé por adulto como mucho.
    if (c.bebes && c.adultos) c.bebes.setAttribute('max', String(num(c.adultos)))

    // Los botones que ya no pueden pulsarse.
    const lleno = totalDe(habitacion) >= TOPE_POR_HABITACION
    for (const [input, selector] of pares) {
      if (!input) continue
      const caja = input.closest('.rvb-pasajeros-input')
      if (!caja) continue
      const mas = leer(caja, '.rvb-btn-add')
      const menos = leer(caja, '.rvb-btn-subtract')
      const max = Number(input.getAttribute('max') || 99)
      const min = Number(input.getAttribute('min') || 0)
      if (mas) mas.disabled = lleno || num(input) >= max
      if (menos) menos.disabled = num(input) <= min
    }
    edades(habitacion)
  }

  /**
   * Crea o quita los campos de edad para que haya uno por niño y uno por bebé, conservando
   * lo que el cliente ya hubiera escrito.
   */
  function edades (habitacion) {
    const casos = [
      { input: '.rvb-childrenAmount', caja: '[data-rvb="inputs-edades-hotel"]', max: EDAD_MAX_NINO, clase: 'rvb-inputEdadNinosHotelClass' },
      { input: '.rvb-babiesAmount', caja: '[data-rvb="inputs-edades-tren-hotel"]', max: EDAD_MAX_BEBE, clase: 'rvb-inputEdadNinosTrenClass' },
    ]
    for (const caso of casos) {
      const caja = leer(habitacion, caso.caja)
      if (!caja) continue
      const cuantos = num(leer(habitacion, caso.input))
      const actuales = [...caja.querySelectorAll('input')]
      // sobran
      actuales.slice(cuantos).forEach((i) => i.closest('.rvb-edadesNinosHotel, div').remove())
      // faltan
      for (let i = actuales.length; i < cuantos; i++) {
        const fila = document.createElement('div')
        fila.className = 'rvb-edadesNinosHotel'
        const etiqueta = document.createElement('label')
        etiqueta.textContent = buscador.texto(caso.max === EDAD_MAX_BEBE ? 7241 : 7239)
        const campo = document.createElement('input')
        campo.type = 'number'
        campo.className = caso.clase
        campo.min = '0'
        campo.max = String(caso.max)
        campo.placeholder = '0-' + caso.max
        campo.setAttribute('aria-label', etiqueta.textContent)
        campo.addEventListener('input', () => revisarListo())
        fila.appendChild(etiqueta)
        fila.appendChild(campo)
        caja.appendChild(fila)
      }
    }
  }

  /** Una edad vale si está escrita y cae dentro de su rango. */
  function edadesCompletas () {
    if (!panel) return true
    const campos = [...panel.querySelectorAll('.rvb-inputEdadNinosHotelClass, .rvb-inputEdadNinosTrenClass')]
    return campos.every((c) => {
      const v = c.value.trim()
      if (v === '') return false
      const n = Number(v)
      return Number.isInteger(n) && n >= 0 && n <= Number(c.max)
    })
  }

  function revisarListo () {
    if (!panel) return
    const listo = panel.querySelector('.rvb-btn-listo-pasajeros')
    if (listo) listo.disabled = !edadesCompletas()
  }

  function habitaciones () {
    return panel ? [...panel.querySelectorAll('.rvb-lista-pasajeros')] : []
  }

  /** Añade o quita habitaciones, hasta el `max` del markup. */
  function cambiarHabitaciones (delta) {
    const input = panel.querySelector('.rvb-buscadorHabitaciones')
    if (!input) return
    const max = Number(input.getAttribute('max') || 4)
    const min = Number(input.getAttribute('min') || 1)
    const cuantas = Math.min(max, Math.max(min, num(input) + delta))
    input.value = String(cuantas)
    panel.querySelectorAll('.rvb-hotelNumRooms').forEach((e) => {
      if (e.tagName === 'SPAN') e.textContent = String(cuantas)
    })

    const contenedor = panel.querySelector('.rvb-roomsContent')
    const actuales = habitaciones()
    if (contenedor) {
      if (cuantas > actuales.length) {
        for (let i = actuales.length; i < cuantas; i++) {
          const nueva = buscador.dom.plantilla('habitacion')
          if (!nueva) break
          // La plantilla trae la habitación envuelta en un <div>; se saca de dentro.
          const dentro = nueva.querySelector('.rvb-lista-pasajeros') || nueva
          contenedor.appendChild(dentro)
        }
      } else {
        actuales.slice(cuantas).forEach((h) => h.remove())
      }
    }
    // Numerar las habitaciones y repintarlas.
    habitaciones().forEach((h, i) => {
      const n = leer(h, '.rvb-roomNumber')
      if (n) n.textContent = String(i + 1)
      pintarHabitacion(h)
    })
    const mas = panel.querySelector('[data-rvb="btnAddRoom"]')
    const menos = panel.querySelector('[data-rvb="btnSubtractRoom"]')
    if (mas) mas.disabled = cuantas >= max
    if (menos) menos.disabled = cuantas <= min
    revisarListo()
  }

  /** Un +1 o -1 en un contador de personas. */
  function cambiarContador (btn, delta) {
    const caja = btn.closest('.rvb-pasajeros-input')
    const habitacion = btn.closest('.rvb-lista-pasajeros')
    if (!caja || !habitacion) return
    const input = caja.querySelector('input[type="hidden"]')
    if (!input) return
    const max = Number(input.getAttribute('max') || 99)
    const min = Number(input.getAttribute('min') || 0)
    const nuevo = num(input) + delta

    if (nuevo < min || nuevo > max) return
    if (delta > 0 && totalDe(habitacion) >= TOPE_POR_HABITACION) return
    input.value = String(nuevo)

    // Al quitar adultos hay que quitar bebés: no puede haber más bebés que adultos.
    const c = contadores(habitacion)
    if (input === c.adultos && c.bebes && num(c.bebes) > nuevo) c.bebes.value = String(nuevo)

    pintarHabitacion(habitacion)
    revisarListo()
  }

  /** Lo que se enseña en el botón cuando se cierra el modal. */
  function resumen (datos) {
    // Los plurales del diccionario son IDS de traducción, no textos: hay que resolverlos.
    const t = (clave, porDefecto) => buscador.texto(clave) || porDefecto
    const trozos = []
    if (datos.habitaciones) {
      trozos.push(datos.habitaciones + ' ' + (datos.habitaciones > 1
        ? t('texto_habitaciones', 'Habitaciones')
        : t('texto_habitacion', 'Habitación')))
    }
    if (datos.adultos) trozos.push(datos.adultos + ' ' + t('texto_adultos', 'Adultos'))
    const ninos = datos.ninos + datos.bebes
    if (ninos) trozos.push(ninos + ' ' + t('texto_childs', 'Niños'))
    return trozos.join(', ')
  }

  /** Recoge del modal lo que el cliente ha elegido. */
  function recoger () {
    const salida = {
      habitaciones: 0, adultos: 0, ninos: 0, bebes: 0,
      edades: [], porHabitacion: [],
    }
    const input = panel.querySelector('.rvb-buscadorHabitaciones')
    salida.habitaciones = input ? num(input) : habitaciones().length

    const edadesBebes = []
    const edadesNinos = []
    habitaciones().forEach((h) => {
      const c = contadores(h)
      const hab = {
        adultsCount: num(c.adultos),
        childrenCount: num(c.ninos) + num(c.bebes),
        childrenAges: [],
      }
      salida.adultos += num(c.adultos)
      salida.ninos += num(c.ninos)
      salida.bebes += num(c.bebes)
      // El orden es el que produce la URL de hoy: antes los del tren (bebés), después los
      // del hotel (niños). Verificado en F0, escenario H: ["2","8"], el bebé primero.
      const deTren = [...h.querySelectorAll('.rvb-inputEdadNinosTrenClass')].map((i) => i.value.trim())
      const deHotel = [...h.querySelectorAll('.rvb-inputEdadNinosHotelClass')].map((i) => i.value.trim())
      hab.childrenAges = deTren.concat(deHotel)
      edadesBebes.push(...deTren)
      edadesNinos.push(...deHotel)
      salida.porHabitacion.push(hab)
    })
    salida.edades = edadesBebes.concat(edadesNinos)
    // La edad del conductor, que sólo traen los dos modales del tren + coche. Viaja en la URL
    // como `driverAge`.
    const conductor = panel.querySelector('[data-rvb="edadConductor"]')
    if (conductor) salida.edadConductor = num(conductor)
    return salida
  }

  /**
   * @param {object} [opciones]
   * @param {string} [opciones.plantilla]   cuál de los dos modales de pasajeros
   * @param {Function} [opciones.alTerminar] qué hacer al pulsar "Listo"
   */
  function abrir (opciones = {}) {
    if (opciones.alTerminar) alTerminar = opciones.alTerminar
    if (opciones.plantilla) plantillaActual = opciones.plantilla
    if (enLinea) {
      // Desplegable del propio bloque: se abre y se cierra, sin modal. El CSS lo deja oculto
      // de serie (.menu-desplegable { display: none }), así que abrir es ponerle la clase.
      if (enLinea.classList.contains('rvb-abierto')) { enLinea.classList.remove('rvb-abierto'); return }
      enLinea.classList.add('rvb-abierto')
      panel = enLinea
      // La lista de habitaciones nace VACÍA en el markup: la home la rellena la primera vez
      // que se abre, clonando la plantilla de habitación. Si no, no hay nada que contar.
      const contenedor = panel.querySelector('.rvb-roomsContent')
      if (contenedor && !contenedor.querySelector('.rvb-lista-pasajeros')) {
        const primera = buscador.dom.plantilla('habitacion')
        const dentro = primera && (primera.querySelector('.rvb-lista-pasajeros') || primera)
        if (dentro) contenedor.appendChild(dentro)
      }
    } else {
      if (modal) { modal.cerrar(); modal = null; return }
      modal = modalConPlantilla(buscador, plantillaActual, 'rvb-swal-pasajeros')
      if (!modal) return
      panel = modal.contenido
    }
    if (panel.dataset.rvbCableado !== '1') {
      panel.dataset.rvbCableado = '1'
      cablear()
    }
    habitaciones().forEach(pintarHabitacion)
    revisarListo()
  }

  /** Los botones de dentro del panel, sea el desplegable o el modal. */
  function cablear () {
    panel.addEventListener('click', (e) => {
      const masHab = e.target.closest('[data-rvb="btnAddRoom"]')
      const menosHab = e.target.closest('[data-rvb="btnSubtractRoom"]')
      if (masHab) { e.preventDefault(); cambiarHabitaciones(1); return }
      if (menosHab) { e.preventDefault(); cambiarHabitaciones(-1); return }
      const mas = e.target.closest('.rvb-btn-add')
      const menos = e.target.closest('.rvb-btn-subtract')
      if (mas) { e.preventDefault(); cambiarContador(mas, 1); return }
      if (menos) { e.preventDefault(); cambiarContador(menos, -1) }
    })

    const listo = panel.querySelector('.rvb-btn-listo-pasajeros')
    if (listo) {
      listo.addEventListener('click', (e) => {
        e.preventDefault()
        if (!edadesCompletas()) return
        estado.pasajeros = recoger()
        const texto = buscador.nodo(bloque, '.rvb-resumen-pasajeros')
        if (texto) texto.textContent = resumen(estado.pasajeros)
        cerrar()
        buscador.revisar(bloque)
        // En el flujo de "Tren + Hotel / Ocio", pulsar Listo no cierra un desplegable: es el
        // último paso de la búsqueda y lo siguiente es irse a comprar.
        if (alTerminar) { const f = alTerminar; alTerminar = null; f() }
      })
    }
    const cancelar = panel.querySelector('.rvb-btn-cancelar-pasajeros, .rvb-btn-cancelar')
    if (cancelar) cancelar.addEventListener('click', (e) => { e.preventDefault(); cerrar() })

    // Las edades se escriben a mano: cada cambio puede habilitar o bloquear el botón.
    panel.addEventListener('input', (e) => {
      if (e.target.matches('.rvb-inputEdadNinosHotelClass, .rvb-inputEdadNinosTrenClass')) revisarListo()
    })
  }

  function cerrar () {
    if (modal) { modal.cerrar(); modal = null; panel = null }
    else if (enLinea) enLinea.classList.remove('rvb-abierto')
  }

  if (boton) boton.addEventListener('click', (e) => { e.preventDefault(); abrir() })

  return {
    abrir,
    /** Para que F3 pueda pedir lo elegido sin abrir el modal. */
    leerEstado: () => estado.pasajeros,
    destruir () { cerrar() },
  }
}
